import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { mitErsatz } from "@/office/makro/pruefung";
import { hochgestellt, readZeichen, tiefgestellt } from "@/office/word/zeichen";
import { MAKRO_DOKUMENT } from "../makros";

/** Wörter, die offensichtlich in Großbuchstaben getippt statt formatiert wurden. */
function getippteGrossbuchstaben(texte: string[]): string[] {
  return texte.filter((wort) => {
    const buchstaben = wort.replace(/[^\p{L}]/gu, "");
    return buchstaben.length >= 6 && buchstaben === buchstaben.toLocaleUpperCase("de-DE");
  });
}

export const lektionZeichen: Lesson = {
  id: "zeichen",
  title: "Hochgestellt, Kapitälchen und der Formatpinsel",
  summary: "Die Zeichenformate, die über fett und kursiv hinausgehen – und wie man sie überträgt.",
  durationMinutes: 9,
  learningGoals: [
    "Hoch- und Tiefstellen als Format statt als Ersatzzeichen einsetzen",
    "Kapitälchen und Großbuchstaben als Format statt getippt verwenden",
    "Formatierungen mit dem Formatpinsel übertragen",
  ],
  steps: [
    {
      id: "hoch-tief",
      title: "Hoch- und tiefgestellt",
      explanation: [
        "Quadratmeter, Fußnotenzeichen, chemische Formeln: Manche Zeichen sitzen über oder " +
          "unter der Grundlinie. Word hat dafür zwei Zeichenformate auf der Registerkarte " +
          "„Start“ in der Gruppe „Schriftart“ – die Schaltflächen x² und x₂.",
        "Schneller geht es mit der Tastatur: Strg + Umschalt + Plus stellt hoch, Strg + # " +
          "stellt tief. Ein zweiter Druck auf dieselbe Kombination schaltet wieder zurück.",
        "Der Unterschied zu einer kleineren Schrift, die man von Hand nach oben schiebt: " +
          "Hochgestellt bleibt ein Format. Ändert sich die Schriftgröße des Absatzes, wandert " +
          "das Zeichen mit und bleibt im richtigen Verhältnis.",
        "In derselben Gruppe sitzt auch „Durchgestrichen“ (abc). Es markiert Text, der " +
          "sichtbar entfallen soll – etwa einen alten Preis. Für echte Überarbeitungen ist " +
          "die Änderungsnachverfolgung aus dem Modul „Zusammenarbeit“ das bessere Werkzeug.",
      ],
      keyPoints: [
        "Strg + Umschalt + Plus = hochgestellt, Strg + # = tiefgestellt.",
        "Hoch- und Tiefstellen sind Formate, keine verschobenen Buchstaben.",
        "Durchgestrichen zeigt Entfallenes – Überarbeitungen gehören in die Nachverfolgung.",
      ],
      task: {
        instruction:
          "Schreibe „Die Wohnung hat 85 m2 und der Raum enthält CO2.“ und stelle die 2 in " +
          "„m2“ hoch und die 2 in „CO2“ tief.",
        hint: "Nur die Ziffer markieren, dann Strg + Umschalt + Plus bzw. Strg + #.",
        check: async () => {
          const zeichen = await readZeichen();
          if (zeichen.runs.length === 0) {
            return fail(
              "Im Dokument steht noch kein Text.",
              "Schreib den Beispielsatz und formatiere danach die beiden Ziffern.",
            );
          }
          const hoch = hochgestellt(zeichen);
          const tief = tiefgestellt(zeichen);
          return fromCriteria(
            [
              criterion("Mindestens ein Zeichen ist hochgestellt (etwa in m²)", hoch.length > 0),
              criterion("Mindestens ein Zeichen ist tiefgestellt (etwa in CO₂)", tief.length > 0),
            ],
            {
              success: "Beide Formate sitzen – und wandern mit, wenn sich die Schriftgröße ändert.",
              failure: "Eines der beiden Formate fehlt noch.",
              hint: "Achte darauf, nur die Ziffer zu markieren, nicht das ganze Wort.",
            },
          );
        },
      },
    },
    {
      id: "kapitaelchen",
      title: "Kapitälchen statt getippter Großbuchstaben",
      explanation: [
        "Kapitälchen sind Großbuchstaben in der Höhe der Kleinbuchstaben. Sie heben Namen " +
          "hervor, ohne so laut zu wirken wie fetter Text, und sind in Verträgen, " +
          "Literaturangaben und Briefköpfen verbreitet.",
        "Eingestellt werden sie im Dialog „Schriftart“ (Strg + D) oder direkt mit " +
          "Strg + Umschalt + Q. Im selben Dialog steht „Großbuchstaben“ (Strg + Umschalt + G): " +
          "Der Text wird in Großbuchstaben angezeigt, bleibt aber so gespeichert, wie er " +
          "getippt wurde.",
        "Warum das wichtig ist: Ein in Großbuchstaben getipptes Wort lässt sich nicht mehr " +
          "zurückverwandeln, Screenreader buchstabieren es manchmal, und wer später doch " +
          "normale Schreibung möchte, muss alles neu tippen. Als Format ist die Entscheidung " +
          "jederzeit umkehrbar – oder zentral in einer Formatvorlage festgelegt.",
        "Für getippten Text, der schon falsch dasteht, gibt es „Start“ → „Groß-/" +
          "Kleinschreibung“ (Aa) oder Umschalt + F3, das zwischen den Schreibweisen wechselt.",
      ],
      keyPoints: [
        "Strg + Umschalt + Q = Kapitälchen, Strg + Umschalt + G = Großbuchstaben.",
        "Als Format bleibt die Schreibweise umkehrbar – getippt nicht.",
        "Umschalt + F3 wechselt bei getipptem Text zwischen den Schreibweisen.",
      ],
      task: {
        instruction:
          "Schreibe einen Namen (zum Beispiel deinen eigenen) in normaler Schreibweise und " +
          "formatiere ihn als Kapitälchen. Im Dokument soll kein Wort mit sechs oder mehr " +
          "Buchstaben komplett in Großbuchstaben getippt sein.",
        hint: "Namen markieren, Strg + D, Häkchen bei „Kapitälchen“.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => {
              const zeichen = await readZeichen();
              if (!zeichen.desktopDetails) return null;
              return {
                kapitaelchen: zeichen.runs.filter((run) => run.smallCaps !== false).length,
                getippt: getippteGrossbuchstaben(
                  zeichen.runs.filter((run) => run.smallCaps === false && run.allCaps === false).map((run) => run.text),
                ),
              };
            },
            MAKRO_DOKUMENT,
            (bericht) => ({ kapitaelchen: bericht.zahl("kapitaelchen") ?? 0, getippt: [] as string[] }),
            ({ kapitaelchen, getippt }) =>
              fromCriteria(
                [
                  criterion("Text in Kapitälchen gefunden", kapitaelchen > 0),
                  criterion(
                    getippt.length === 0
                      ? "Keine in Großbuchstaben getippten Wörter"
                      : `In Großbuchstaben getippt: ${getippt.slice(0, 3).join(", ")}`,
                    getippt.length === 0,
                  ),
                ],
                {
                  success: "Kapitälchen als Format – die Schreibweise bleibt jederzeit umkehrbar.",
                  failure: "Noch nicht ganz.",
                  hint:
                    "Getippte Großbuchstaben markieren, mit Umschalt + F3 in normale Schreibung " +
                    "bringen und danach als Kapitälchen formatieren.",
                },
              ),
          ),
      },
    },
    {
      id: "formatpinsel",
      title: "Format übertragen mit dem Pinsel",
      explanation: [
        "Der Pinsel ganz links auf der Registerkarte „Start“ heißt „Format übertragen“. " +
          "Ein Klick darauf nimmt die Formatierung der aktuellen Stelle auf; der nächste " +
          "markierte Text bekommt sie übertragen.",
        "Ein Doppelklick auf den Pinsel hält ihn aktiv, sodass sich dieselbe Formatierung " +
          "auf mehrere Stellen nacheinander übertragen lässt. Esc beendet das. Mit der " +
          "Tastatur geht es über Strg + Umschalt + C (Format kopieren) und " +
          "Strg + Umschalt + V (Format einfügen).",
        "Der Pinsel ist praktisch, aber er ist direkte Formatierung. Wer merkt, dass er " +
          "dieselbe Kombination immer wieder überträgt, sollte daraus eine Formatvorlage " +
          "machen – dann ändert eine Anpassung alle Stellen auf einmal.",
      ],
      keyPoints: [
        "Einfacher Klick auf den Pinsel: einmal übertragen. Doppelklick: mehrfach, Esc beendet.",
        "Strg + Umschalt + C / V kopiert und fügt nur die Formatierung ein.",
        "Wiederkehrende Formatierungen gehören in eine Formatvorlage.",
      ],
    },
  ],
};
